import { Flex, Form, Switch, Tooltip, Typography } from "antd"
import React, { useId } from "react"

import { PERMISSION_DEPENDENCIES } from "../helpers/dependencies.ts"
import { getDisplayEntry } from "../helpers/utils.ts"
import { type PermissionsMode } from "../types.ts"
import type {
  AnyPermissionKey,
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"

const { Text } = Typography

type GridCell = { rowLabel: string; columnLabel: string }

type PermissionsActionSwitchProps = {
  parentKey: keyof PermissionFeatures
  displayMap?: PermissionDisplayMap | null
  subKey: string
  actionKey: string
  value: boolean
  isUpdating?: boolean
  // Only a grid cell names itself: feature rows and extras show the name in a label beside it.
  cell?: GridCell
} & PermissionsMode

type CaptionedProps = {
  caption?: string
  description?: string
  descriptionId: string
  children: React.ReactNode
}

const getDepLabel = (depKey: string): string =>
  depKey
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

const Captioned = ({
  caption,
  description,
  descriptionId,
  children
}: CaptionedProps) => {
  if (!caption && !description) return <>{children}</>

  return (
    <Flex vertical align="center" gap={2}>
      {children}
      {caption && (
        <Text type="secondary" className="text-xs text-center leading-tight">
          {caption}
        </Text>
      )}
      {description && (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      )}
    </Flex>
  )
}

export const PermissionsActionSwitch = (
  props: PermissionsActionSwitchProps
) => {
  const descriptionId = useId()
  const deps = PERMISSION_DEPENDENCIES[props.actionKey as AnyPermissionKey]
  const requires = deps?.length
    ? `Requires: ${deps.map((depKey) => getDisplayEntry(props.displayMap, depKey)?.name || getDepLabel(depKey)).join(", ")}`
    : undefined

  const ownEntry = props.cell
    ? getDisplayEntry(props.displayMap, props.actionKey)
    : undefined
  const caption = ownEntry?.name
  const description = ownEntry?.tooltip

  const tooltipTitle =
    description && requires ? (
      <>
        {description}
        <br />
        {requires}
      </>
    ) : (
      description || requires
    )

  const accessibility = props.cell
    ? {
        "aria-label":
          caption || `${props.cell.rowLabel} — ${props.cell.columnLabel}`,
        "aria-describedby": description ? descriptionId : undefined
      }
    : {}

  const captionProps = { caption, description, descriptionId }

  if (props.asForm) {
    return (
      <Captioned {...captionProps}>
        <Tooltip title={tooltipTitle}>
          {/* span needed: Form.Item with noStyle doesn't forward hover events to Tooltip */}
          <span className="inline-flex justify-center">
            <Form.Item
              name={[props.parentKey, props.subKey, props.actionKey]}
              valuePropName="checked"
              initialValue={props.value}
              noStyle
            >
              <Switch size="small" {...accessibility} />
            </Form.Item>
          </span>
        </Tooltip>
      </Captioned>
    )
  }

  const { parentKey, subKey, actionKey, value, isUpdating, onItemChange } =
    props

  const handleChange = (v: boolean) =>
    onItemChange(
      parentKey,
      subKey as keyof PermissionFeatures[typeof parentKey],
      actionKey as keyof PermissionFeatures[typeof parentKey][keyof PermissionFeatures[typeof parentKey]],
      v
    )

  return (
    <Captioned {...captionProps}>
      <Tooltip title={tooltipTitle}>
        <span className="inline-flex justify-center">
          <Switch
            size="small"
            checked={value}
            onChange={handleChange}
            disabled={isUpdating}
            {...accessibility}
          />
        </span>
      </Tooltip>
    </Captioned>
  )
}
